import { useState, useEffect, useCallback } from 'react';
import api, { getImageUrl } from '../lib/api';
import { iterateSectionCards, cardCode, cardName, cardShort, cardImage } from '../lib/grouping';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Download, Image as ImageIcon, Loader2 } from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';

export default function CatalogPreviewModal({ catalog, open, onClose, onDownloadPdf }) {
  const [translatedData, setTranslatedData] = useState(null);
  const [translating, setTranslating] = useState(false);

  const fetchTranslations = useCallback(async () => {
    if (!catalog || !open) return;
    const lang = catalog.language || 'en';
    if (lang === 'en') { setTranslatedData(null); return; }
    setTranslating(true);
    try {
      const { data } = await api.post('/catalogs/translate-preview', catalog);
      setTranslatedData(data);
    } catch { setTranslatedData(null); }
    setTranslating(false);
  }, [catalog, open]);

  useEffect(() => { fetchTranslations(); }, [fetchTranslations]);

  if (!catalog) return null;

  const branding = catalog.branding || {};
  const primaryColor = branding.primary_color || '#C5A059';
  const companyName = branding.company_name || '';
  const tagline = branding.tagline || '';
  const lang = catalog.language || 'en';
  const backCover = catalog.back_cover || {};
  const td = translatedData || catalog;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent aria-describedby="catalog-preview-desc" className="max-w-4xl h-[90vh] p-0 bg-[#FDFBF7] border-[#E5E0D8] rounded-sm overflow-hidden" data-testid="catalog-preview-modal">
        <DialogTitle className="sr-only">Catalog Preview — {catalog.title}</DialogTitle>
        <DialogDescription id="catalog-preview-desc" className="sr-only">Preview of the catalog as it will appear when shared, including all sections, products, and MOQ pricing.</DialogDescription>
        <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-[#E5E0D8]">
          <div>
            <h3 className="font-serif text-lg text-[#1A1A1A]">Catalog Preview</h3>
            <p className="text-xs text-[#5C5C5C]">{catalog.title}{lang !== 'en' ? ` (Bilingual)` : ''}</p>
          </div>
          <div className="flex gap-2 items-center">
            {translating && <span className="text-xs text-[#C5A059] flex items-center gap-1"><Loader2 size={12} className="animate-spin" />Translating...</span>}
            {onDownloadPdf && <Button size="sm" onClick={onDownloadPdf} className="bg-[#C5A059] hover:bg-[#B38F48] text-white rounded-sm text-xs" data-testid="preview-download-pdf"><Download size={14} className="mr-1" />Export PDF</Button>}
          </div>
        </div>

        <ScrollArea className="flex-1 h-[calc(90vh-60px)]">
          {/* Premium Cover — reference: logo band → title band → full image */}
          <div className="relative bg-[#FDFBF7]" data-testid="preview-cover">
            {/* Logo Band */}
            <div className="relative bg-[#141414] py-10 px-6 text-center" style={{ borderLeft: `4px solid ${primaryColor}`, borderRight: `4px solid ${primaryColor}` }}>
              <img src={branding.logo_url ? getImageUrl(branding.logo_url) : '/brand/aarya-logo-gold-on-black.png'} alt="AARYA24kt" className="h-24 md:h-28 mx-auto object-contain" />
            </div>
            {/* Title Band */}
            <div className="px-8 pt-10 pb-6 text-center bg-[#FDFBF7]">
              <h1 className="font-serif font-bold tracking-tight leading-tight text-4xl md:text-5xl" style={{ color: '#4a3a1f' }}>{catalog.title}</h1>
              {td._title_trans && <p className="font-serif text-2xl mt-2" style={{ color: '#8a6b30' }}>{td._title_trans}</p>}
              {catalog.subtitle && <p className="text-xs tracking-[0.3em] uppercase mt-4 text-[#5C5C5C]">{catalog.subtitle}</p>}
              {td._subtitle_trans && <p className="text-xs italic text-[#888] mt-1">{td._subtitle_trans}</p>}
              <div className="w-24 h-px mx-auto my-5" style={{ background: primaryColor }} />
              {catalog.intro_note && <p className="text-sm text-[#5C5C5C] max-w-xl mx-auto leading-relaxed">{catalog.intro_note}</p>}
              {catalog.catalog_type === 'corporate' && catalog.corporate_meta?.client_company && (
                <div className="max-w-xl mx-auto mt-5 pt-4 border-t border-[#E5E0D8] text-xs text-[#5C5C5C]" data-testid="preview-corp-cover-meta">
                  <p className="text-sm" style={{ color: '#4a3a1f' }}>Prepared for: <strong style={{ color: primaryColor }}>{catalog.corporate_meta.client_company}</strong></p>
                  {(catalog.corporate_meta.contact_person || catalog.corporate_meta.validity_date) && (
                    <p className="mt-1">
                      {catalog.corporate_meta.contact_person && <span>Contact: {catalog.corporate_meta.contact_person}</span>}
                      {catalog.corporate_meta.contact_person && catalog.corporate_meta.validity_date && ' · '}
                      {catalog.corporate_meta.validity_date && <span>Valid until {catalog.corporate_meta.validity_date}</span>}
                    </p>
                  )}
                </div>
              )}
            </div>
            {/* Cover Image — bounded height, object-contain, centered (iter28). */}
            <div className="w-full bg-[#F5F2EB] flex items-center justify-center px-6 py-8" data-testid="preview-cover-image">
              {catalog.cover_image ? (
                <img src={getImageUrl(catalog.cover_image)} alt="Cover" className="block mx-auto object-contain w-auto h-auto max-w-[75%] max-h-[45vh]" />
              ) : (
                <div className="w-full max-w-[70%] mx-auto flex items-center justify-center font-serif text-4xl tracking-[0.4em] rounded-sm" style={{ background: 'linear-gradient(135deg, #2a1f10 0%, #141414 100%)', color: primaryColor, minHeight: '28vh', maxHeight: '40vh', aspectRatio: '4 / 3' }}>
                  {companyName || 'AARYA24kt'}
                </div>
              )}
            </div>
          </div>

          {/* TOC */}
          {td.sections?.length > 0 && (
            <div className="py-8 px-8 bg-[#FDFBF7]">
              <h2 className="font-serif text-xl text-center mb-6" style={{ color: primaryColor }}>Contents</h2>
              <div className="max-w-md mx-auto">
                {td.sections.map((s, i) => (
                  <div key={s.id || i} className="flex justify-between items-center py-2 border-b border-[#E5E0D8]">
                    <div>
                      <span className="text-sm text-[#1A1A1A]">{s.name}</span>
                      {s._name_trans && <span className="text-xs text-[#888] italic ml-2">{s._name_trans}</span>}
                    </div>
                    <span className="text-xs text-[#5C5C5C]">{s.products?.length || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sections */}
          {td.sections?.map((section, sIdx) => (
            <div key={section.id || sIdx} data-testid={`preview-section-${sIdx}`}>
              <div className="py-12 px-8 bg-[#FDFBF7] text-center">
                <div className="flex items-center justify-center gap-4">
                  <div className="w-12 h-px" style={{ background: primaryColor }} />
                  <div>
                    <h2 className="font-serif text-2xl" style={{ color: primaryColor }}>{section.name}</h2>
                    {section._name_trans && <p className="text-base text-[#888] italic mt-1">{section._name_trans}</p>}
                  </div>
                  <div className="w-12 h-px" style={{ background: primaryColor }} />
                </div>
              </div>
              <div className="px-8 pb-8">
                <div className="grid grid-cols-2 gap-6 items-start auto-rows-max">
                  {iterateSectionCards(section.products).map((card, pIdx) => {
                    const { product, isGroup } = card;
                    const pt = product._translations || {};
                    const displayCode = cardCode(card);
                    const displayName = cardName(card);
                    const displayShort = cardShort(card);
                    const displayImage = cardImage(card);
                    const showPrices = catalog.show_prices !== false;
                    return (
                    <div key={isGroup ? `g-${product.design_group_id}` : (product.id || pIdx)} className="bg-white border border-[#E5E0D8] rounded-sm overflow-hidden" data-testid={`preview-product-${sIdx}-${pIdx}`}>
                      <div className="aspect-[4/3] bg-[#F5F2EB] flex items-center justify-center">
                        {displayImage ? <img src={getImageUrl(displayImage)} alt={displayName} className="w-full h-full object-cover" /> : <ImageIcon size={32} className="text-[#E5E0D8]" />}
                      </div>
                      <div className="p-4">
                        <p className="text-[9px] tracking-[0.2em] uppercase mb-1" style={{ color: primaryColor }}>{displayCode}</p>
                        <h3 className="text-sm font-medium text-[#1A1A1A]">{displayName}</h3>
                        {!isGroup && pt.product_name && <p className="text-xs text-[#888] italic">{pt.product_name}</p>}
                        {displayShort && <p className="text-[11px] text-[#5C5C5C] mt-1 line-clamp-2">{displayShort}</p>}
                        {!isGroup && pt.short_description && <p className="text-[10px] text-[#aaa] italic line-clamp-2">{pt.short_description}</p>}
                        {isGroup ? (
                          // Available Sizes table (Product Master V2). Same
                          // columns as the shared page + PDF for a consistent
                          // preview → export experience.
                          <div className="mt-3 pt-2 border-t border-[#E5E0D8]" data-testid={`preview-sizes-${sIdx}-${pIdx}`}>
                            <p className="text-[9px] tracking-[0.2em] uppercase mb-1.5" style={{ color: primaryColor }}>Available Sizes</p>
                            <table className="w-full text-[11px] border-collapse">
                              <thead>
                                <tr style={{ borderBottom: `1px solid ${primaryColor}` }}>
                                  <th className="text-left py-1 pr-2 font-semibold uppercase tracking-wide text-[9px] text-[#1A1A1A]">SKU</th>
                                  <th className="text-left py-1 pr-2 font-semibold uppercase tracking-wide text-[9px] text-[#1A1A1A]">Size</th>
                                  {showPrices && <th className="text-right py-1 font-semibold uppercase tracking-wide text-[9px] text-[#1A1A1A]">MRP</th>}
                                  {catalog.show_quantity && <th className="text-right py-1 pl-2 font-semibold uppercase tracking-wide text-[9px] text-[#1A1A1A]">Qty</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {(product._group_siblings || []).map((sib) => (
                                  <tr key={sib.product_code} className="border-b border-[#F0EBE2] last:border-b-0">
                                    <td className="py-1 pr-2 font-mono text-[10px] whitespace-nowrap" style={{ color: primaryColor }}>{sib.product_code}</td>
                                    <td className="py-1 pr-2 text-[#5C5C5C]">{sib.size || '—'}</td>
                                    {showPrices && <td className="py-1 text-right font-medium text-[#1A1A1A] whitespace-nowrap">{sib._display_price || '—'}</td>}
                                    {catalog.show_quantity && (
                                      sib._quantity_available
                                        ? <td className="py-1 pl-2 text-right whitespace-nowrap"><span className="inline-block bg-[#F2E8D5] text-[#3a2d14] text-[10px] font-semibold rounded-sm px-2 py-0.5">{sib._quantity_display}</span></td>
                                        : <td className="py-1 pl-2 text-right whitespace-nowrap"><span className="inline-block bg-[#F5F2EB] text-[#8a7a5c] text-[10px] rounded-sm px-2 py-0.5">Qty unavailable</span></td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="flex justify-between items-center pt-2 mt-2 border-t border-[#E5E0D8]">
                            {product.size && <span className="text-[10px] text-[#5C5C5C]">{product.size}</span>}
                            <div className="flex items-center gap-1.5 ml-auto">
                              {product._display_price && <span className="text-sm font-medium text-[#1A1A1A]">{product._display_price}</span>}
                              {catalog.show_quantity && (
                                product._quantity_available
                                  ? <span className="inline-block bg-[#F2E8D5] text-[#3a2d14] text-[10px] font-semibold rounded-sm px-2 py-0.5" data-testid={`preview-qty-badge-${sIdx}-${pIdx}`}>{product._quantity_display}</span>
                                  : <span className="inline-block bg-[#F5F2EB] text-[#8a7a5c] text-[10px] rounded-sm px-2 py-0.5" data-testid={`preview-qty-missing-${sIdx}-${pIdx}`}>Qty unavailable</span>
                              )}
                            </div>
                          </div>
                        )}
                        {catalog.catalog_type === 'corporate' && catalog.show_prices !== false && product._moq_tiers?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-[#E5E0D8]" data-testid={`preview-moq-${sIdx}-${pIdx}`}>
                            <p className="text-[9px] tracking-[0.15em] uppercase font-semibold mb-1" style={{ color: primaryColor }}>MOQ Pricing</p>
                            <table className="w-full text-[10px] border-collapse">
                              <thead>
                                <tr className="border-b" style={{ borderColor: primaryColor }}>
                                  <th className="text-left py-0.5 pr-1 font-semibold text-[#1A1A1A]">MOQ</th>
                                  <th className="text-left py-0.5 pr-1 font-semibold text-[#1A1A1A]">Price/pc</th>
                                  {product._moq_tiers.some(t => t.delivery_days) && <th className="text-left py-0.5 font-semibold text-[#1A1A1A]">Delivery</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {product._moq_tiers.map((t, i) => (
                                  <tr key={`prev-tier-${t.moq_quantity}-${i}`} className="border-b border-[#F0EBE2]">
                                    <td className="py-0.5 pr-1 font-semibold whitespace-nowrap text-[#1A1A1A]">{Number(t.moq_quantity || 0).toLocaleString()}</td>
                                    <td className="py-0.5 pr-1 font-semibold whitespace-nowrap" style={{ color: '#4a3a1f' }}>{t.calculated_price_display}</td>
                                    {product._moq_tiers.some(tt => tt.delivery_days) && <td className="py-0.5 whitespace-nowrap text-[#1A1A1A]">{t.delivery_days_display || '—'}</td>}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}

          {/* Back Cover — always rendered with brand logo */}
          <div className="bg-[#141414] py-16 px-8 text-center" data-testid="preview-back-cover">
            <img src={branding.logo_url ? getImageUrl(branding.logo_url) : '/brand/aarya-logo-gold-on-black.png'} alt="AARYA24kt" className="h-24 md:h-32 mx-auto object-contain" />
            <div className="w-16 h-px mx-auto my-6" style={{ background: primaryColor }} />
            {backCover.name && <p className="font-serif text-xl mb-3" style={{ color: primaryColor }}>{backCover.name}</p>}
            {backCover.phone && <p className="text-xs text-[#E5E0D8] mb-1 tracking-wide">{backCover.phone}</p>}
            {backCover.email && <p className="text-xs text-[#E5E0D8] mb-1 tracking-wide">{backCover.email}</p>}
            {backCover.website && <p className="text-xs text-[#E5E0D8] mb-1 tracking-wide">{backCover.website}</p>}
            {backCover.address && <p className="text-xs text-[#E5E0D8] mt-3 whitespace-pre-line max-w-md mx-auto leading-relaxed">{backCover.address}</p>}
            {!backCover.name && catalog.footer_contact && <p className="text-xs text-[#E5E0D8] whitespace-pre-line max-w-md mx-auto">{catalog.footer_contact}</p>}
            {!backCover.name && !catalog.footer_contact && <p className="text-xs italic tracking-[0.2em]" style={{ color: primaryColor }}>{tagline || 'Gold Foil Artwork Collection'}</p>}
            <p className="font-serif text-sm tracking-[0.4em] uppercase mt-10" style={{ color: primaryColor }}>Thank you</p>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
